import { useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, Radio } from 'lucide-react';
import {
  HelpAnchor,
  SkeletonBlock,
  SkeletonCardGrid,
  SurfacePageBody,
  SurfacePageLayout,
} from '@/design-system';
import { Commentable } from '@/shared/comments/Commentable';
import { AskStrip } from '@/versions/v1/chrome-oem/AskStrip';
import { useDrawer } from '@/app/providers';
import { unpackTable } from '@/versions/v1/shared/columnar';
import { useOemData } from '@/versions/v1/shared/useOemData';
import { useOemPersona } from '@/versions/v1/shared/OemPersonaContext';
import { useDemoMode } from '@/versions/v1/shared/DemoModeContext';
import { routeForView } from '@/versions/v1/shared/oem-routes';
import { issueSummary, jobs, todayItems } from '@/versions/v1/shared/oem-engine';
import { act, type OemAction } from '@/versions/v1/shared/seed/action';
import type { TodayAction } from '@/versions/v1/shared/oem-engine';
import type { WorkOrder } from '@/versions/v1/schema/work-order';
import type { ShiftDailyRow } from '@/versions/v1/schema/telemetry';
import type { CustomerSatisfactionResponse } from '@/versions/v1/schema/person';
import {
  MeasureTile,
  SceneSection,
  SourceLink,
  TonePill,
  type MeasureTone,
} from '@/versions/v1/shared/oem-tiles';
import { toneColor } from '@/versions/v1/shared/viz/tokens';
import { Disclosure } from '@/versions/v1/shared/Disclosure';
import { useMaximoRecords } from '../integration/useMaximoRecords';
import { Asset360OperatingModel } from './Asset360OperatingModel';
import {
  agentChips,
  auditSlice,
  connectorChips,
  controlTower,
  fleetRulesLine,
  hero,
  howThisWorks,
  inactionSource,
  ledgerSource,
  loopTiles,
  moduleTiles,
  outcomeCards,
  peakModuleJobs,
  siteCards,
  systemChips,
  towerMaximoIds,
} from './Asset360Scene.transform';

// =========================================================================
// Asset360 — the executive landing. Demo `renderAsset360`, source line 2990.
// Fork-owned (zone C). Route `/`.
//
// Reading order is the argument, and it is not the demo's. The demo opens on
// its control tower because it was built to show a platform. This page opens
// on two sentences and two numbers, because it was built to open a
// conversation: *we can see the whole cost of running this fleet, and we can
// see the failures before they land*. Everything under the fold is the proof
// — the eight measures, the live issues, the persona's morning, the ten
// modules, the five loops, the six sites, the systems and the agents.
//
// Density is deliberately front-loaded light: four figures above the fold at
// 1440×900, then the detail. Jay's brief (plan §Context, 22 Sep) is to lead
// executive and keep detail as drill-down.
//
// Every figure opens its source. That is the demo's own promise and the
// reason `oem-source` exists as a drawer kind.
//
// Demo mode. Executive (the default) is the simplified page described above.
// Full restores the demo's depth: the four How-this-works count cards give
// way to the ten module cards, the five loop cards, the system families and
// the agent chips (`Asset360OperatingModel`), and the Operational view band
// opens by default (`Disclosure`'s `openInFull`).
// =========================================================================

/** Domains this page needs. All six fetch in parallel; none blocks paint. */
function useAsset360Data() {
  const wos = useOemData('work-orders');
  const telemetry = useOemData('telemetry');
  const service = useOemData('service');
  const people = useOemData('people');
  const parts = useOemData('parts');
  const costs = useOemData('costs');

  const states = [wos, telemetry, service, people, parts, costs];
  const loading = states.some((s) => s.loading);
  const error = states.find((s) => s.error)?.error ?? null;

  const ctx = useMemo(() => {
    if (
      !wos.data ||
      !telemetry.data ||
      !service.data ||
      !people.data ||
      !parts.data ||
      !costs.data
    ) {
      return null;
    }
    return {
      workOrders: unpackTable<WorkOrder>(wos.data.wos),
      shiftDaily: unpackTable<ShiftDailyRow>(telemetry.data.shift_daily),
      techJobs: service.data.tapp_jobs,
      cases: service.data.cases,
      training: service.data.lms,
      contracts: service.data.mx_contracts,
      techs: people.data.tapp_techs,
      css: unpackTable<CustomerSatisfactionResponse>(people.data.css),
      inventory: parts.data.inventory,
      parts: parts.data.parts,
      orders: parts.data.orders,
      bulletins: parts.data.bulletins,
      timesheets: costs.data.timesheets,
      costByMachineCat: costs.data.cost_by_machine_cat,
      // No `maximo` here: the per-machine pack is 1.6 MB across 100 files and
      // only the control tower reads it, so `ControlTowerSection` loads it
      // when the tower mounts. See `controlTower()`.
    };
  }, [wos.data, telemetry.data, service.data, people.data, parts.data, costs.data]);

  return { ctx, loading, error };
}

type Asset360Ctx = NonNullable<ReturnType<typeof useAsset360Data>['ctx']>;

/**
 * The eight control-tower measures, with the Maximo pack loaded on mount.
 *
 * A component of its own so the 100 per-machine Maximo records are fetched
 * only when the tower is on screen: it lives inside the Operational view
 * band, which mounts its children only when open. In Executive mode that is
 * a click; in Full mode the band opens by default and the pack loads with the
 * page. Until every record is in, the "Contracts & commercial" tile says the
 * counts are coming rather than printing a partial one (H10).
 */
function ControlTowerSection({
  ctx,
  go,
}: {
  ctx: Asset360Ctx | null;
  go: (action: OemAction) => void;
}) {
  const ids = useMemo(towerMaximoIds, []);
  const { records, loading } = useMaximoRecords(ids);
  const tower = useMemo(
    () => (ctx ? controlTower({ ...ctx, maximo: records }, loading) : null),
    [ctx, records, loading],
  );

  return (
    <Commentable name="ControlTower">
      <SceneSection
        title="Global aftermarket control tower"
        subtitle="real-time visibility · AI-driven decisions · P&L optimisation"
        actions={
          <span
            className="flex items-center gap-3 text-[11px]"
            style={{ color: 'var(--ink-tertiary)' }}
          >
            {(['good', 'warn', 'bad'] as MeasureTone[]).map((tone, i) => (
              <span key={tone} className="flex items-center gap-1.5">
                <span
                  aria-hidden="true"
                  className="inline-block h-2 w-2 rounded-[2px]"
                  style={{ background: toneColor(tone) }}
                />
                {['on track', 'watch', 'needs action'][i]}
              </span>
            ))}
          </span>
        }
      >
        {tower ? (
          <div
            className="grid grid-cols-1 gap-3 @xl:grid-cols-2 @5xl:grid-cols-4"
            aria-busy={tower.maximo === 'loading'}
          >
            {tower.tiles.map((tile) => (
              <MeasureTile
                key={tile.label}
                eyebrow={tile.label}
                value={tile.value}
                unit={tile.unit}
                detail={tile.detail}
                tone={tile.tone as MeasureTone}
                onSelect={() => go(tile.action)}
                selectHint="Opens the surface behind this measure."
              />
            ))}
          </div>
        ) : (
          <SkeletonCardGrid count={8} cols={3} />
        )}
      </SceneSection>
    </Commentable>
  );
}

/** fork(L3): the reports index — `routeForView('reports')`. */
const REPORTS_ACTION: OemAction = act('reports');

export function Asset360Scene() {
  const navigate = useNavigate();
  const { open: openDrawer } = useDrawer();
  const { personaId, persona } = useOemPersona();
  const { mode } = useDemoMode();
  const full = mode === 'full';
  const { ctx, loading, error } = useAsset360Data();

  /** Every cross-reference on this page resolves through `oem-routes.ts`. */
  const go = useCallback(
    (action: OemAction) => {
      const route = routeForView(action.view, action.sub);
      if (route) navigate(route);
    },
    [navigate],
  );

  const showSource = useCallback(
    (data: Record<string, unknown>, title: string) => {
      openDrawer('oem-source', { title, kicker: 'Source', wide: true, data });
    },
    [openDrawer],
  );

  const view = useMemo(() => {
    if (!ctx) return null;
    const openJobs = jobs(ctx);
    const modules = moduleTiles(openJobs);
    const loops = loopTiles();
    const agents = agentChips();
    const connectorCount = connectorChips().length;
    return {
      hero: hero(ctx, openJobs.length),
      issues: issueSummary(ctx),
      today: todayItems(personaId, ctx),
      modules,
      outcomes: outcomeCards(ctx),
      sites: siteCards(openJobs),
      // B1: the four intelligence-layer sections became four counts in
      // Executive mode. Full mode renders the lists themselves.
      howThisWorks: howThisWorks({
        modules: modules.length,
        loops: loops.length,
        connectors: connectorCount,
        agents: agents.length,
      }),
      loops,
      fleetRules: fleetRulesLine(),
      systems: systemChips(),
      connectorCount,
      agents,
    };
  }, [ctx, personaId]);

  /** A Today item's action, dispatched by kind. */
  const runTodayAction = useCallback(
    (action: TodayAction) => {
      switch (action.kind) {
        case 'view':
          go(action.action);
          return;
        case 'ask':
          // The assistant lane (P4) listens for this; until it lands the event
          // is simply unheard, which is the right degradation for a chip.
          window.dispatchEvent(new CustomEvent('oem:ask', { detail: action.prompt }));
          return;
        case 'audit': {
          if (!ctx) return;
          const slice = auditSlice(
            action.dataset,
            action.filter,
            {
              cases: ctx.cases as unknown as Record<string, unknown>[],
              inv: ctx.inventory as unknown as Record<string, unknown>[],
              parts: ctx.parts as unknown as Record<string, unknown>[],
              wos: ctx.workOrders as unknown as Record<string, unknown>[],
            },
            action.predicate,
          );
          showSource(slice as unknown as Record<string, unknown>, slice.title);
          return;
        }
        case 'doc':
          // Lake documents live on the data browser; the reference travels as
          // a query so the browser can select it once P5 lands that surface.
          navigate(`/integration/data?ref=${encodeURIComponent(action.ref)}`);
      }
    },
    [ctx, go, navigate, showSource],
  );

  return (
    <SurfacePageLayout width="full">
      <Commentable name="Asset360Scene">
        <SurfacePageBody className="@container max-w-[1320px] space-y-10 pt-6">
          {error ? (
            <div
              role="alert"
              className="rounded-xl border p-4 text-sm"
              style={{
                borderColor: 'var(--hairline)',
                background: 'var(--crit-soft)',
                color: 'var(--crit)',
              }}
            >
              The aftermarket data could not be loaded ({error.message}). Every figure on
              this page comes from that data, so nothing is shown rather than something
              wrong.
            </div>
          ) : null}

          {/* ---------------------------------------------------------- */}
          {/* Hero — two outcomes, two numbers                           */}
          {/* ---------------------------------------------------------- */}
          <Commentable name="Hero">
            <header className="min-w-0">
              <div className="flex flex-wrap items-start justify-between gap-x-8 gap-y-3">
                <div className="min-w-0 max-w-3xl">
                  {/* B2: the breadcrumb already says Asset360. */}
                  <h1
                    className="text-[30px] font-semibold leading-[1.15] tracking-tight"
                    style={{ color: 'var(--ink-primary)' }}
                  >
                    Every machine and every dollar, in one place
                  </h1>
                  <p
                    className="mt-2.5 text-[13.5px] leading-relaxed"
                    style={{ color: 'var(--ink-secondary)' }}
                  >
                    The intelligence layer sits over the systems the fleet already runs
                    on.
                  </p>
                </div>
                {/*
                  One wrapping row, never a column. Stacking these three right-
                  aligned looks correct beside a short title and strands them
                  mid-column the moment the header wraps; a row reads the same
                  either way — chips beside the title, or a line under it.
                */}
                <div className="flex shrink-0 flex-wrap items-center gap-x-3 gap-y-1.5">
                  {view ? (
                    <>
                      <TonePill tone="good">{view.hero.platformLine}</TonePill>
                      <span className="text-[11px]" style={{ color: 'var(--ink-tertiary)' }}>
                        {view.hero.statusLine}
                      </span>
                    </>
                  ) : (
                    <SkeletonBlock className="h-10 w-56" />
                  )}
                  <HelpAnchor doc="asset360/overview" />
                </div>
              </div>

              <div className="mt-6 grid grid-cols-1 gap-4 @4xl:grid-cols-2">
                {view
                  ? view.hero.outcomes.map((outcome, i) => (
                      <article
                        key={outcome.kicker}
                        className="min-w-0 rounded-2xl border p-5"
                        style={{
                          borderColor: 'var(--hairline)',
                          background: 'var(--surface-2)',
                        }}
                      >
                        <span
                          className="text-[10px] font-semibold uppercase tracking-[0.12em]"
                          style={{ color: 'var(--accent)' }}
                        >
                          {outcome.kicker}
                        </span>
                        <p
                          className="mt-1.5 text-[14.5px] font-medium leading-snug"
                          style={{ color: 'var(--ink-primary)' }}
                        >
                          {outcome.claim}
                        </p>
                        <div className="mt-4 flex flex-wrap items-baseline gap-x-3 gap-y-1">
                          <SourceLink
                            onClick={() => {
                              if (!ctx) return;
                              const slice = i === 0 ? ledgerSource(ctx) : inactionSource();
                              showSource(
                                slice as unknown as Record<string, unknown>,
                                slice.title,
                              );
                            }}
                            className="mono !text-[38px] !font-semibold leading-none !tracking-tight !no-underline hover:!underline"
                          >
                            {outcome.figure}
                          </SourceLink>
                          <span
                            className="text-[12px]"
                            style={{ color: 'var(--ink-secondary)' }}
                          >
                            {outcome.unit}
                          </span>
                        </div>
                        <ul className="mt-4 space-y-1.5">
                          {outcome.support.map((line) => (
                            <li
                              key={line}
                              className="flex gap-2 text-[12px] leading-[1.55]"
                              style={{ color: 'var(--ink-tertiary)' }}
                            >
                              <span aria-hidden="true" style={{ color: 'var(--accent)' }}>
                                ·
                              </span>
                              <span className="min-w-0">{line}</span>
                            </li>
                          ))}
                        </ul>
                        <button
                          type="button"
                          onClick={() => go(outcome.action)}
                          className="mt-4 inline-flex items-center gap-1.5 rounded text-[12px] font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color-mix(in_srgb,var(--accent)_55%,transparent)]"
                          style={{ color: 'var(--accent)' }}
                        >
                          {outcome.actionLabel}
                          <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
                        </button>
                        {/* fork(oem/v1.8): the priced failures, ranked — the driver tree. */}
                        {i === 1 ? (
                          <Link
                            to="/priorities"
                            className="ml-5 mt-4 inline-flex items-center gap-1.5 rounded text-[12px] font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color-mix(in_srgb,var(--accent)_55%,transparent)]"
                            style={{ color: 'var(--accent)' }}
                          >
                            Which machine first?
                            <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
                          </Link>
                        ) : null}
                      </article>
                    ))
                  : [0, 1].map((i) => (
                      <SkeletonBlock key={i} className="h-[264px] rounded-2xl" />
                    ))}
              </div>

              {/*
                C5. The `Global aftermarket control tower` tile strip used to
                sit here and state a capability. This states the change the
                buyer is being sold. The eight measures are not gone — they
                are the first thing inside the Operational view band at the
                foot of the page.
              */}
              <p
                className="mt-6 max-w-3xl text-[14px] font-medium leading-relaxed"
                style={{ color: 'var(--ink-secondary)' }}
              >
                Today those two numbers live in a dozen systems that were never
                introduced to each other, and the failure is found after it lands.
                The future-state operating model reconciles them nightly and reads
                the machine ahead of the failure — so the argument stops being about
                whose spreadsheet is right and starts being about what to do on
                Tuesday.
              </p>
            </header>
          </Commentable>

          <AskStrip view="asset360" />

          {/* ---------------------------------------------------------- */}
          {/* Live issues                                                */}
          {/* ---------------------------------------------------------- */}
          <Commentable name="IssueSummary">
            <SceneSection
              title="Live issues"
              subtitle="what is open on the fleet this morning"
              actions={
                view ? (
                  <>
                    <SourceLink onClick={() => go(view.issues.allIssues)}>
                      All issues
                    </SourceLink>
                    {/* fork(L3): the one way into the generated reports from the
                        landing — a link, not a section, in both modes. */}
                    <SourceLink onClick={() => go(REPORTS_ACTION)}>Reports</SourceLink>
                  </>
                ) : null
              }
            >
              {view ? (
                <>
                  <div className="grid grid-cols-1 gap-3 @lg:grid-cols-2 @5xl:grid-cols-4">
                    {view.issues.tiles.map((tile) => (
                      <MeasureTile
                        key={tile.eyebrow}
                        eyebrow={tile.eyebrow}
                        value={tile.value}
                        detail={tile.detail}
                        tone={tile.tone as MeasureTone}
                        onSelect={() => go(tile.action)}
                        selectHint="Opens the full list."
                      />
                    ))}
                  </div>
                  <ul
                    className="mt-3 overflow-hidden rounded-xl border"
                    style={{ borderColor: 'var(--hairline)', background: 'var(--surface-2)' }}
                  >
                    {view.issues.rows.map((row, i) => (
                      <li
                        key={row.alert.id}
                        style={{
                          borderTop: i ? '1px solid var(--hairline)' : undefined,
                        }}
                      >
                        <button
                          type="button"
                          onClick={() => go(row.action)}
                          className="flex w-full flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2.5 text-left transition-colors hover:bg-[var(--surface-1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[color-mix(in_srgb,var(--accent)_55%,transparent)]"
                        >
                          <TonePill tone={row.crit ? 'bad' : 'warn'}>
                            {row.crit ? 'Critical' : 'Warning'}
                          </TonePill>
                          <span
                            className="mono text-[12px] font-medium"
                            style={{ color: 'var(--ink-primary)' }}
                          >
                            {row.machine.machine_id}
                          </span>
                          <span
                            className="min-w-0 flex-1 truncate text-[12.5px]"
                            style={{ color: 'var(--ink-secondary)' }}
                          >
                            {row.alert.title}
                          </span>
                          <span
                            className="text-[11px]"
                            style={{ color: 'var(--ink-tertiary)' }}
                          >
                            {row.site} · {row.alert.raised}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </>
              ) : (
                <SkeletonCardGrid count={4} cols={2} />
              )}
            </SceneSection>
          </Commentable>

          {/* ---------------------------------------------------------- */}
          {/* Sites                                                      */}
          {/* ---------------------------------------------------------- */}
          <Commentable name="Sites">
            <SceneSection
              title="Mine sites"
              subtitle="six customer operations across five states"
              actions={
                <SourceLink onClick={() => navigate('/sites')}>Sites map</SourceLink>
              }
            >
              {view ? (
                <div className="grid grid-cols-1 gap-3 @xl:grid-cols-2 @4xl:grid-cols-3">
                  {view.sites.map((site) => (
                    <button
                      key={site.code}
                      type="button"
                      onClick={() => navigate(`/sites/${site.code}`)}
                      className="min-w-0 rounded-xl border p-4 text-left transition-colors hover:bg-[var(--surface-1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color-mix(in_srgb,var(--accent)_55%,transparent)]"
                      style={{
                        borderColor: 'var(--hairline)',
                        background: 'var(--surface-2)',
                      }}
                    >
                      <span className="flex items-center justify-between gap-2">
                        <span
                          className="truncate text-[13px] font-semibold"
                          style={{ color: 'var(--ink-primary)' }}
                        >
                          {site.name}
                        </span>
                        {site.live ? (
                          <TonePill tone="good">
                            <Radio className="h-2.5 w-2.5" aria-hidden="true" />
                            Live
                          </TonePill>
                        ) : (
                          <TonePill tone="flat">Shift-end</TonePill>
                        )}
                      </span>
                      <span
                        className="mt-1 block truncate text-[11px]"
                        style={{ color: 'var(--ink-tertiary)' }}
                      >
                        {site.state} · {site.mining} · {site.machines} machines
                      </span>
                      <span className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11.5px]">
                        <span style={{ color: 'var(--ink-secondary)' }}>
                          <b className="mono" style={{ color: 'var(--band-vpoor)' }}>
                            {site.severe}
                          </b>{' '}
                          very poor or worse
                        </span>
                        <span style={{ color: 'var(--ink-secondary)' }}>
                          <b className="mono" style={{ color: 'var(--ink-primary)' }}>
                            {site.openJobs}
                          </b>{' '}
                          open jobs
                        </span>
                      </span>
                    </button>
                  ))}
                </div>
              ) : (
                <SkeletonCardGrid count={6} cols={3} />
              )}
            </SceneSection>
          </Commentable>

          {/* ---------------------------------------------------------- */}
          {/* Outcomes                                                   */}
          {/* ---------------------------------------------------------- */}
          <Commentable name="Outcomes">
            <SceneSection title="Outcomes" subtitle="what the operating model is for">
              {view ? (
                <div className="grid grid-cols-1 gap-3 @xl:grid-cols-2 @5xl:grid-cols-4">
                  {view.outcomes.map((o) => (
                    <div
                      key={o.title}
                      className="min-w-0 rounded-xl border p-4"
                      style={{
                        borderColor: 'var(--hairline)',
                        background: 'var(--surface-2)',
                      }}
                    >
                      <h3
                        className="text-[12.5px] font-semibold"
                        style={{ color: 'var(--ink-primary)' }}
                      >
                        {o.title}
                      </h3>
                      <p
                        className="mt-1.5 text-[11.5px] leading-[1.55]"
                        style={{ color: 'var(--ink-tertiary)' }}
                      >
                        {o.detail}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <SkeletonCardGrid count={4} cols={2} />
              )}
            </SceneSection>
          </Commentable>

          {/* ---------------------------------------------------------- */}
          {/* How this works                                             */}
          {/* ---------------------------------------------------------- */}
          {/*
            B1. Workflows, Feedback loops, Connectors and the AI agent layer
            were four sections and roughly four screens of cards. They are
            the answer to "why not Power BI on steroids?", so they cannot
            leave the page — but they were arguing with the hero rather than
            supporting it. Four cards carry the same four counts, and each
            one opens the section it summarises. Full mode puts the four
            sections back (Asset360OperatingModel).
          */}
          {full ? (
            view ? (
              <Asset360OperatingModel
                modules={view.modules}
                loops={view.loops}
                fleetRules={view.fleetRules}
                systems={view.systems}
                connectorCount={view.connectorCount}
                agents={view.agents}
                go={go}
              />
            ) : (
              <SkeletonCardGrid count={10} cols={3} />
            )
          ) : (
            <Commentable name="HowThisWorks">
              <SceneSection
                title="How this works"
                subtitle="the operating model under the two numbers above"
              >
                {view ? (
                  <div className="grid grid-cols-1 gap-3 @xl:grid-cols-2 @5xl:grid-cols-4">
                    {view.howThisWorks.map((card) => (
                      <button
                        key={card.title}
                        type="button"
                        onClick={() => navigate(card.route)}
                        aria-label={`${card.title}. ${card.linkLabel}.`}
                        className="flex min-w-0 flex-col rounded-xl border p-4 text-left transition-colors hover:bg-[var(--surface-1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color-mix(in_srgb,var(--accent)_55%,transparent)]"
                        style={{
                          borderColor: 'var(--hairline)',
                          background: 'var(--surface-2)',
                        }}
                      >
                        <span
                          className="text-[10px] font-semibold uppercase leading-4 tracking-[0.1em]"
                          style={{ color: 'var(--ink-tertiary)' }}
                        >
                          {card.title}
                        </span>
                        <span className="mt-1.5 flex flex-wrap items-baseline gap-x-2">
                          <span
                            className="mono text-[26px] font-semibold leading-none tracking-tight"
                            style={{ color: 'var(--ink-primary)' }}
                          >
                            {card.count}
                          </span>
                          <span
                            className="text-[11.5px] leading-tight"
                            style={{ color: 'var(--ink-secondary)' }}
                          >
                            {card.noun}
                          </span>
                        </span>
                        <span
                          className="mt-2 block min-h-0 flex-1 text-[11.5px] leading-[1.5]"
                          style={{ color: 'var(--ink-tertiary)' }}
                        >
                          {card.detail}
                        </span>
                        <span
                          className="mt-3 inline-flex items-center gap-1.5 text-[11.5px] font-medium"
                          style={{ color: 'var(--accent)' }}
                        >
                          {card.linkLabel}
                          <ArrowRight className="h-3 w-3" aria-hidden="true" />
                        </span>
                      </button>
                    ))}
                  </div>
                ) : (
                  <SkeletonCardGrid count={4} cols={2} />
                )}
              </SceneSection>
            </Commentable>
          )}

          {/* ---------------------------------------------------------- */}
          {/* Operational view                                           */}
          {/* ---------------------------------------------------------- */}
          {/*
            B1 and C5. The eight control-tower measures, the persona's
            morning and the module job split are the operational layer. They
            are good, and they are not what an aftermarket executive needs in
            the first five seconds, so they sit behind a closed band instead
            of between the hero and the issues. Nothing is deleted: the band
            says how much is inside, and every measure still opens the
            surface behind it. Full mode opens the band by default.
          */}
          <Disclosure
            label="Operational view"
            count="3 sections · 8 measures"
            hint="The global control tower, today's shift for the active persona, and where the open jobs sit by module."
          >
            {/* ---------------------------------------------------------- */}
            {/* Control tower                                              */}
            {/* ---------------------------------------------------------- */}
            <ControlTowerSection ctx={ctx} go={go} />

            {/* ---------------------------------------------------------- */}
            {/* Today + where the open jobs are                            */}
            {/* ---------------------------------------------------------- */}
            <div className="grid grid-cols-1 gap-6 @4xl:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)]">
              <Commentable name="TodayPanel">
                <SceneSection
                  title={`Today · ${persona.name}`}
                  subtitle={persona.desc}
                >
                  {view ? (
                    <ul
                      className="overflow-hidden rounded-xl border"
                      style={{ borderColor: 'var(--hairline)', background: 'var(--surface-2)' }}
                    >
                      {view.today.map((item, i) => (
                        <li
                          key={item.t}
                          className="flex flex-wrap items-start gap-x-3 gap-y-2 px-4 py-3"
                          style={{ borderTop: i ? '1px solid var(--hairline)' : undefined }}
                        >
                          <span className={`band-dot band-${item.k} mt-1.5`} aria-hidden="true" />
                          <span
                            className="min-w-0 flex-1 text-[12.5px] leading-[1.55]"
                            style={{ color: 'var(--ink-secondary)' }}
                          >
                            {item.t}
                          </span>
                          <span className="flex shrink-0 flex-wrap gap-2">
                            {item.acts.map((act) => (
                              <button
                                key={act.label}
                                type="button"
                                onClick={() => runTodayAction(act)}
                                className="rounded-md border px-2 py-1 text-[11px] font-medium transition-colors hover:bg-[var(--surface-1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color-mix(in_srgb,var(--accent)_55%,transparent)]"
                                style={{
                                  borderColor: 'var(--hairline-strong)',
                                  color: 'var(--ink-primary)',
                                }}
                              >
                                {act.label}
                              </button>
                            ))}
                          </span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <SkeletonBlock className="h-[232px] rounded-xl" />
                  )}
                </SceneSection>
              </Commentable>

              <Commentable name="JobsByModule">
                <SceneSection
                  title="Where the open jobs are"
                  subtitle="every job sits in one module"
                  actions={
                    <SourceLink onClick={() => navigate('/job-board')}>Job board</SourceLink>
                  }
                >
                  {view ? (
                    <ul
                      className="overflow-hidden rounded-xl border"
                      style={{ borderColor: 'var(--hairline)', background: 'var(--surface-2)' }}
                    >
                      {view.modules.map((m, i) => {
                        const peak = peakModuleJobs(view.modules);
                        return (
                          <li
                            key={m.id}
                            style={{ borderTop: i ? '1px solid var(--hairline)' : undefined }}
                          >
                            <button
                              type="button"
                              onClick={() => navigate(`/workflows/modules/${m.id}`)}
                              className="flex w-full items-center gap-3 px-4 py-2 text-left transition-colors hover:bg-[var(--surface-1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[color-mix(in_srgb,var(--accent)_55%,transparent)]"
                            >
                              <span
                                className="mono w-5 shrink-0 text-[11px] font-semibold"
                                style={{ color: m.color }}
                              >
                                {String(m.n).padStart(2, '0')}
                              </span>
                              <span
                                className="w-[152px] shrink-0 truncate text-[12px] font-medium"
                                style={{ color: 'var(--ink-primary)' }}
                              >
                                {m.short}
                              </span>
                              <span
                                className="h-1.5 min-w-0 flex-1 overflow-hidden rounded-full"
                                style={{ background: 'var(--surface-3)' }}
                                aria-hidden="true"
                              >
                                <span
                                  className="block h-full rounded-full"
                                  style={{
                                    width: `${Math.round((m.openJobs / peak) * 100)}%`,
                                    background: m.color,
                                  }}
                                />
                              </span>
                              <span
                                className="mono w-6 shrink-0 text-right text-[11.5px]"
                                style={{ color: 'var(--ink-primary)' }}
                              >
                                {m.openJobs}
                              </span>
                              <span
                                className="hidden w-[128px] shrink-0 truncate text-[11px] @5xl:block"
                                style={{ color: 'var(--ink-tertiary)' }}
                              >
                                {m.sample.join(' · ')}
                                {m.openJobs > 3 ? ' …' : ''}
                              </span>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  ) : (
                    <SkeletonBlock className="h-[232px] rounded-xl" />
                  )}
                </SceneSection>
              </Commentable>
            </div>
          </Disclosure>

          {loading && !view ? (
            <p className="sr-only" role="status">
              Loading the aftermarket data.
            </p>
          ) : null}

          <p className="pt-2 text-[11px]" style={{ color: 'var(--ink-tertiary)' }}>
            Proof of concept · every figure on this page is the 09 Sep 2026 snapshot of
            the demonstration data pack, derived at render time rather than typed in.
          </p>
        </SurfacePageBody>
      </Commentable>
    </SurfacePageLayout>
  );
}

export default Asset360Scene;
